'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountError } from '@akademi/site-builder-runtime';

/**
 * What the account blocks share: one field, one set of styles, and one way of
 * turning a refusal into a sentence.
 */

/** A refusal, in words, by its code — never the server's English, which is not translated. */
export function useAccountErrorText(): (error: unknown) => string {
  const { t } = useTranslation('site-builder-blocks');
  return (error: unknown) => {
    const kind = error instanceof AccountError ? error.kind : 'UNAVAILABLE';
    return t(`commerce.account.errors.${kind}`);
  };
}

/**
 * A same-site path to go to after signing in, or null.
 *
 * <p>`?next=` is attacker-writable — anyone can send a link to
 * `/sign-in?next=https://evil.example` — and following it blindly turns the
 * shop's own sign-in page into a phishing relay that the shopper has just
 * typed their password into. Only a path on this site is honoured: it must
 * start with one `/`, and `//host` (protocol-relative) and `/\host` (which
 * some browsers normalise to the same) are refused.
 */
export function safeNext(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null;
  return value;
}

/** The sign-in page, bringing the shopper back to `here` afterwards when that is a page on this site. */
export function signInHrefFor(signInHref: string, here: string | null): string {
  const next = safeNext(here);
  if (!next) return signInHref;
  return `${signInHref}${signInHref.includes('?') ? '&' : '?'}next=${encodeURIComponent(next)}`;
}

/**
 * Takes a signed-out shopper to the sign-in page, and brings them back.
 *
 * <p>An account page has nothing to show a guest, so a sentence and a button there ask for a click
 * that has only one possible answer. What the shopper asked for was their account; the way to it is
 * sign-in, and `?next=` returns them to the page they wanted.
 *
 * <p>`signedOut` must be the *resolved* answer, never merely "no profile yet". Two states are not a
 * signed-out shopper and must not be passed as one: the account check still being out, which would
 * throw a signed-in shopper out of their own account, and a check that failed, where the shopper may
 * well be signed in and the shop is simply unreachable — redirecting there would hide the fault and
 * lose them the page. Each caller therefore passes its own settled signed-out state.
 *
 * <p>It replaces the current history entry instead of pushing one, so Back from the sign-in page
 * does not land on the page that redirects and bounce straight forward again.
 *
 * <p>The caller still renders a sign-in link. This runs in an effect, so it does not run in a server
 * render or where scripting is unavailable, and the link is what the page is left with there.
 */
export function useSignInRedirect(signedOut: boolean, signInHref: string, here: string | null): void {
  useEffect(() => {
    if (!signedOut || typeof window === 'undefined') return;
    window.location.replace(signInHrefFor(signInHref, here));
  }, [signedOut, signInHref, here]);
}

/** One query parameter, read after mount so the server render and first paint agree. */
export function useQueryParam(name: string): { value: string | null; ready: boolean } {
  const [state, setState] = useState<{ value: string | null; ready: boolean }>({
    value: null,
    ready: false,
  });
  useEffect(() => {
    setState({ value: new URLSearchParams(window.location.search).get(name), ready: true });
  }, [name]);
  return state;
}

export function AccountField({
  id,
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
  required,
  minLength,
  maxLength,
  hint,
  readOnly,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  hint?: string;
  /** Shown, sent, and not to be changed here — an address that comes from the account. */
  readOnly?: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <label htmlFor={id} style={{ fontSize: 14, fontWeight: 500 }}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        maxLength={maxLength}
        readOnly={readOnly}
        aria-describedby={hint ? `${id}-hint` : undefined}
        style={readOnly ? readOnlyInputStyle : inputStyle}
      />
      {hint && (
        <span id={`${id}-hint`} style={hintStyle}>
          {hint}
        </span>
      )}
    </div>
  );
}

/**
 * The base's own rule for a new password, which a reset and a new account's
 * activation both go through: at least this long, with an upper-case letter, a
 * lower-case letter and a digit. A tenant with its own password policy may ask
 * for more on a reset, and says so with `WEAK_PASSWORD`.
 */
export const NEW_PASSWORD_MIN_LENGTH = 8;

export const titleStyle: CSSProperties = { margin: '0 0 16px', fontSize: 'var(--font-size-3xl, 30px)' };
export const leadStyle: CSSProperties = { margin: '0 0 20px' };
export const formStyle: CSSProperties = { display: 'grid', gap: 12, maxWidth: 420 };
export const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm, 6px)',
  border: '1px solid var(--color-border, #e5e7eb)',
  font: 'inherit',
};
export const readOnlyInputStyle: CSSProperties = {
  ...inputStyle,
  background: 'var(--color-surface-muted, #f3f4f6)',
  color: 'var(--color-text-secondary, #6b7280)',
};
export const hintStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--color-text-secondary, #6b7280)',
};
export const buttonStyle: CSSProperties = {
  padding: '12px 24px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-primary, #2563eb)',
  color: 'var(--color-on-primary, #fff)',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  font: 'inherit',
  justifySelf: 'start',
};
export const secondaryButtonStyle: CSSProperties = {
  ...buttonStyle,
  background: 'var(--color-surface-muted, #f3f4f6)',
  color: 'var(--color-text, #111827)',
  border: '1px solid var(--color-border, #e5e7eb)',
};
export const errorStyle: CSSProperties = {
  margin: 0,
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-danger-50, #fef2f2)',
  color: 'var(--color-danger-700, #b91c1c)',
};
export const noticeStyle: CSSProperties = {
  margin: 0,
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-success-50, #ecfdf5)',
  color: 'var(--color-success-700, #047857)',
};
export const mutedStyle: CSSProperties = {
  margin: 0,
  color: 'var(--color-text-secondary, #6b7280)',
};
export const linkRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  marginTop: 16,
  fontSize: 14,
};
